/**
 * The week's job outcomes for the Shift Roster: what happened to each order,
 * which orders continue a partial job, and recording a new outcome.
 */
import { addDays, format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";

import jobOutcomesApi, { JobOutcome, JobOutcomeKind } from "../../../../../services/job-outcomes-api";

export interface JobOutcomeIndex {
	/** Outcome by work order id. */
	byOrder: Map<number, JobOutcome>;
	/** Order Update schedule made (rest of a partial job, rerun of one not done) → the order it follows. */
	continues: Map<number, number>;
}

const EMPTY: JobOutcomeIndex = { byOrder: new Map(), continues: new Map() };

export function useJobOutcomes(weekStart: Date) {
	const weekKey = format(weekStart, "yyyy-MM-dd");
	const [index, setIndex] = useState<JobOutcomeIndex>(EMPTY);
	const [version, setVersion] = useState(0);

	useEffect(() => {
		let mounted = true;
		const to = format(addDays(new Date(`${weekKey}T00:00:00`), 7), "yyyy-MM-dd");
		jobOutcomesApi
			.list(weekKey, to)
			.then((data) => {
				if (!mounted) return;
				setIndex({
					byOrder: new Map(data.outcomes.map((o) => [o.workOrderId, o])),
					continues: new Map(Object.entries(data.continuations).map(([rest, from]) => [Number(rest), from])),
				});
			})
			// A failed read (an API without outcomes, a restart) keeps what the board shows.
			.catch(() => undefined);
		return () => {
			mounted = false;
		};
	}, [weekKey, version]);

	const reload = useCallback(() => setVersion((v) => v + 1), []);

	/** Record (or with null, clear) an outcome; the board shows it at once. */
	const record = useCallback(async (workOrderId: number, outcome: JobOutcomeKind | null, remainingMinutes?: number | null, note?: string | null) => {
		const saved = await jobOutcomesApi.record(workOrderId, outcome, remainingMinutes, note);
		setIndex((current) => {
			const byOrder = new Map(current.byOrder);
			if (saved) byOrder.set(workOrderId, saved);
			else byOrder.delete(workOrderId);
			return { ...current, byOrder };
		});
		return saved;
	}, []);

	return useMemo(() => ({ index, record, reload }), [index, record, reload]);
}

export type JobOutcomesState = ReturnType<typeof useJobOutcomes>;
